import { useState, useEffect } from 'react';
import { Users, Shield, Crown, UserCog, Eye, Mail, Search, AlertCircle, UserPlus } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../lib/api';
import InviteMemberModal from '../../components/InviteMemberModal';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  roleInOrg: 'OWNER' | 'ADMIN' | 'MANAGER' | 'STAFF' | 'VIEWER';
  createdAt: string;
  _count?: {
    jobAssignments: number;
  };
}

const ROLE_CONFIG = {
  OWNER: {
    icon: Crown,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    label: 'Owner',
    description: 'Full system access and ownership',
  },
  ADMIN: {
    icon: Shield,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    label: 'Admin',
    description: 'All permissions except ownership transfer',
  },
  MANAGER: {
    icon: UserCog,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    label: 'Manager',
    description: 'Manage campaigns and assign staff',
  },
  STAFF: {
    icon: Users,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    label: 'Staff',
    description: 'Work on assigned campaigns',
  },
  VIEWER: {
    icon: Eye,
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    label: 'Viewer',
    description: 'Read-only access',
  },
};

const ROLE_ORDER = ['OWNER', 'ADMIN', 'MANAGER', 'STAFF', 'VIEWER'];

export default function CorporateTeam() {
  const { user } = useAuthStore();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('ALL');
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizations/${user?.organizationId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!confirm(`Are you sure you want to change this member's role to ${ROLE_CONFIG[newRole as keyof typeof ROLE_CONFIG].label}?`)) {
      return;
    }

    try {
      setChangingRole(memberId);
      await api.patch(`/users/${memberId}`, { roleInOrg: newRole });
      await fetchTeamMembers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update role');
    } finally {
      setChangingRole(null);
    }
  };

  const handleInviteSuccess = () => {
    setShowInviteModal(false);
    fetchTeamMembers();
  };

  const canManageRoles = user?.roleInOrg === 'OWNER' || user?.roleInOrg === 'ADMIN';
  const canInviteMembers = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.roleInOrg || '');

  const filteredMembers = members
    .filter((member) => {
      const matchesSearch =
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === 'ALL' || member.roleInOrg === selectedRole;
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      const roleA = ROLE_ORDER.indexOf(a.roleInOrg);
      const roleB = ROLE_ORDER.indexOf(b.roleInOrg);
      return roleA - roleB;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Team Management</h1>
          <p className="text-glass-text-2 text-sm mt-1">Manage your organization&apos;s members and their roles</p>
        </div>
        {canInviteMembers && (
          <button
            type="button"
            onClick={() => setShowInviteModal(true)}
            className="btn-accent flex items-center gap-2 px-4 py-2 text-sm font-semibold"
          >
            <UserPlus className="w-5 h-5" />
            Invite Member
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ROLE_ORDER.map((role) => {
          const config = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
          const Icon = config.icon;
          const count = members.filter((m) => m.roleInOrg === role).length;

          return (
            <div
              key={role}
              className={`glass-card-tight p-4 cursor-pointer transition-all hover:-translate-y-0.5 ${selectedRole === role ? 'ring-2 ring-accent2/40' : ''}`}
              onClick={() => setSelectedRole(selectedRole === role ? 'ALL' : role)}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${config.color}`} />
                <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
              </div>
              <div className="stat-display text-2xl text-glass-text">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-glass-text-3 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="glass-input w-full pl-10 pr-4 py-2"
            />
          </div>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="glass-input px-4 py-2"
          >
            <option value="ALL">All Roles</option>
            {ROLE_ORDER.map((role) => (
              <option key={role} value={role}>
                {ROLE_CONFIG[role as keyof typeof ROLE_CONFIG].label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Permission Notice */}
      {!canManageRoles && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">View-Only Mode</p>
            <p className="text-sm text-yellow-700 mt-1">
              Only Owners and Admins can modify team member roles.
            </p>
          </div>
        </div>
      )}

      {/* Team Members List */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase tracking-wider">
                  Joined
                </th>
                {canManageRoles && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMembers.map((member) => {
                const roleConfig = ROLE_CONFIG[member.roleInOrg];
                const Icon = roleConfig.icon;
                const isCurrentUser = member.id === user?.id;

                return (
                  <tr key={member.id} className="hover:bg-glass-3/40 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${roleConfig.bg} flex items-center justify-center`}>
                          <span className={`text-lg font-semibold ${roleConfig.color}`}>
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {member.name}
                            {isCurrentUser && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                You
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${roleConfig.color}`} />
                        <div>
                          <div className={`text-sm font-medium ${roleConfig.color}`}>
                            {roleConfig.label}
                          </div>
                          <div className="text-xs text-gray-500">{roleConfig.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {new Date(member.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    {canManageRoles && (
                      <td className="px-6 py-4">
                        {isCurrentUser ? (
                          <span className="text-xs text-gray-400 italic">Cannot modify own role</span>
                        ) : (
                          <select
                            value={member.roleInOrg}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            disabled={changingRole === member.id}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                          >
                            {ROLE_ORDER.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_CONFIG[role as keyof typeof ROLE_CONFIG].label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No team members found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <InviteMemberModal
          organizationId={user?.organizationId || ''}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </div>
  );
}

